import React from 'react'

import {isEmpty} from 'es-toolkit/compat'
import invariant from 'invariant'
import {Route, Routes, useMatch, useNavigate} from 'react-router'

import {useAppDispatch, useAppSelector} from '../../hooks/redux'
import {signOut} from '../../redux/features/auth'
import {setProvider} from '../../redux/features/core'

import useAsync from '../../../../commons/src/hooks/use-async'
import {anyPermission, anyPermissionExceptOperator} from '../../libs/user'
import * as internalApi from '../../services/api/internal'
import * as partnerApi from '../../services/api/partner'
import * as providerApi from '../../services/api/provider'
import {captureException} from '../../services/logging'
import {goToSettings} from '../../services/navigation'
import {RequireAuth, isPermitted} from '../utils/routing'

import {ProviderRatingAggregated} from '../../../../commons/src/types'
import {Permission, SettingsTab} from '../../types'

import awardIcon from '../../../../commons/src/assets/icons/award.svg'
import refreshIcon from '../../../../commons/src/assets/icons/refresh.svg'
import signOutIcon from '../../../../commons/src/assets/icons/sign-out.svg'

import ActionBar from '../../../../commons/src/components/action-bar/ActionBar'
import Badge from '../../../../commons/src/components/badge/Badge'
import Button from '../../../../commons/src/components/button'
import DescriptionList from '../../../../commons/src/components/description-list'
import DescriptionListItem from '../../../../commons/src/components/description-list/DescriptionListItem'
import Headline from '../../../../commons/src/components/headline'
import Icon from '../../../../commons/src/components/icon'
import LinkButton from '../../../../commons/src/components/link-button'
import LoadingIndicator from '../../../../commons/src/components/loading-indicator'
import Paragraph from '../../../../commons/src/components/paragraph'
import Stack from '../../../../commons/src/components/stack'
import StarRating from '../../../../commons/src/components/star-rating'
import Tab from '../../../../commons/src/components/tabs/Tab'
import Tabs from '../../../../commons/src/components/tabs/Tabs'
import ToggleField from '../../../../commons/src/components/toggle-field/toggle-field'
import Tooltip from '../../../../commons/src/components/tooltip'
import TooltipHandler from '../../../../commons/src/components/tooltip-handler'
import Container from '../../components/container'
import PageContentLayout from '../../components/page-content-layout'
import PageHeader from '../../components/page-content-layout/PageHeader'

import ROUTES from '../../routes'
import PagePartial from '../page-partial'

import SettingsApiKeyPartial from './settings-api-key-partial'
import SettingsGeneralPartial from './settings-general-partial'
import SettingsProfilePartial from './settings-profile-partial'

const routes = [
  {
    path: SettingsTab.GENERAL,
    component: SettingsGeneralPartial,
    requireAuth: true,
    requiredPermissions: anyPermission,
  },
  {
    path: SettingsTab.PROFILE,
    component: SettingsProfilePartial,
    requireAuth: true,
    requiredPermissions: anyPermissionExceptOperator,
  },
  {
    path: SettingsTab.API_KEY,
    component: SettingsApiKeyPartial,
    requireAuth: true,
    requiredPermissions: [Permission.PROVIDER],
  },
]

const SettingsPage = () => {
  const dispatch = useAppDispatch()

  const authUser = useAppSelector(state => state.auth.user)
  const isAdminMode = useAppSelector(state => state.auth.isAdminMode)
  const provider = useAppSelector(state => state.core.provider)

  invariant(provider, 'Provider is missing.')

  const [providerRating, setProviderRating] = React.useState<ProviderRatingAggregated>()
  const [isSubmittingOnlineStatus, setIsSubmittingOnlineStatus] = React.useState(false)

  const navigate = useNavigate()
  const match = useMatch<'tab', string>({
    path: `${ROUTES.SETTINGS}/:tab`,
  })

  const activeTab = match?.params.tab || SettingsTab.GENERAL

  const {
    isLoading: isLoadingProviderRating,
    refresh: receiveProviderRating,
    error: providerRatingError,
  } = useAsync(
    () => {
      if (isAdminMode) {
        return internalApi.getAggregatedReview(provider.id)
      }
      return partnerApi.getAggregatedReview()
    },
    [],
    {
      onError: error => {
        captureException(error)
      },
      onDone: data =>
        setProviderRating(
          isEmpty(data)
            ? {
                total: 0,
                count: 0,
                quality: 0,
                leadTime: 0,
                valueForMoney: 0,
                communication: 0,
              }
            : data
        ),
    }
  )

  const updateOnlineStatus = async (onlineStatus: boolean) => {
    try {
      setIsSubmittingOnlineStatus(true)
      const nextProvider = await providerApi.patch({online: onlineStatus}, provider.id)
      dispatch(setProvider({provider: nextProvider}))
    } catch (error) {
      captureException(error)
    } finally {
      setIsSubmittingOnlineStatus(false)
    }
  }

  const isTabDisabled = (tab: SettingsTab) => {
    const routeForTab = routes.find(route => route.path === tab)

    return (
      !!authUser &&
      (!routeForTab || !isPermitted(authUser.permission, routeForTab.requiredPermissions))
    )
  }

  const renderLabel = (label: string) => (
    <Paragraph size="s" className="u-no-margin-bottom">
      {label}
    </Paragraph>
  )

  const renderProviderRating = () => {
    if (!providerRating && isLoadingProviderRating) {
      return <LoadingIndicator />
    }
    if (!providerRating && providerRatingError) {
      return (
        <ActionBar>
          <Paragraph size="xs" warning className="u-no-margin-bottom">
            Loading total rating failed
          </Paragraph>
          <LinkButton
            icon={<Icon source={refreshIcon} />}
            onClick={() => {
              receiveProviderRating()
            }}
          />
        </ActionBar>
      )
    }
    if (providerRating) {
      return (
        <Stack>
          <Stack space="80">
            <ActionBar alignItems="bottom" space="300">
              <Paragraph strong className="u-no-margin-bottom">
                Your total rating
              </Paragraph>
              <StarRating
                stars={providerRating.total}
                suffix={`${providerRating.total} out of 5`}
                size="l"
              />
            </ActionBar>
            <Paragraph minor size="xs" className="u-no-margin-bottom">
              {`${providerRating.count} reviews in total`}
            </Paragraph>
          </Stack>
          <DescriptionList noRowGap>
            <DescriptionListItem label={renderLabel('Quality')}>
              <StarRating stars={providerRating.quality} />
            </DescriptionListItem>
            <DescriptionListItem label={renderLabel('Lead time')}>
              <StarRating stars={providerRating.leadTime} />
            </DescriptionListItem>
            <DescriptionListItem label={renderLabel('Value for money')}>
              <StarRating stars={providerRating.valueForMoney} />
            </DescriptionListItem>
            <DescriptionListItem label={renderLabel('Communication')}>
              {providerRating.communication ? (
                <StarRating stars={providerRating.communication} />
              ) : (
                <Paragraph className="u-no-margin-bottom" minor size="xs">
                  not rated
                </Paragraph>
              )}
            </DescriptionListItem>
          </DescriptionList>
        </Stack>
      )
    }

    return null
  }

  return (
    <PagePartial fixedHeight>
      <PageContentLayout
        fixedHeight
        sidebar={<Container>{renderProviderRating()}</Container>}
        header={
          <PageHeader
            headline={
              <ActionBar space="300">
                <Headline size="l" label="Profile Settings" className="u-no-margin-bottom" />
                {provider.premium && (
                  <TooltipHandler
                    tooltip={
                      <Tooltip appearance="dark">
                        You have been awarded the Craftcloud badge of{' '}
                        <strong>Excellence Partners</strong> - the criteria to receive the badge of{' '}
                        <strong>Excellence</strong> are based on quality of delivered orders,
                        minimum order delays and the amount of successfully delivered orders via
                        Craftcloud.
                      </Tooltip>
                    }
                  >
                    <Badge label="Excellence" type="gold" icon={<Icon source={awardIcon} />} />
                  </TooltipHandler>
                )}

                <TooltipHandler
                  tooltip={
                    provider.online ? (
                      <Tooltip appearance="dark">
                        Pause your service on Craftcloud for vacation or other circumstances.
                        <br />
                        Your offers will not show up on Craftcloud.
                      </Tooltip>
                    ) : (
                      <Tooltip appearance="dark">
                        Your service is currently offline.
                        <br />
                        Set yourself back online to show up on Craftcloud and receive new orders.
                      </Tooltip>
                    )
                  }
                >
                  <ActionBar>
                    <ToggleField
                      emphasized
                      checked={provider.online}
                      label={
                        provider.online ? (
                          <Paragraph success className="u-no-margin-bottom">
                            Online
                          </Paragraph>
                        ) : (
                          <Paragraph error className="u-no-margin-bottom">
                            Offline
                          </Paragraph>
                        )
                      }
                      onChange={value => {
                        updateOnlineStatus(value)
                      }}
                    />
                    {isSubmittingOnlineStatus && <LoadingIndicator />}
                  </ActionBar>
                </TooltipHandler>
              </ActionBar>
            }
            actions={
              <Button
                hierarchy="secondary"
                onClick={() => dispatch(signOut())}
                icon={<Icon source={signOutIcon} />}
              >
                Sign out
              </Button>
            }
          >
            <Tabs
              activeTab={activeTab}
              onChange={tabKey =>
                goToSettings(navigate, {
                  activeTab: tabKey as SettingsTab,
                  replace: true,
                })
              }
            >
              <Tab
                label="General"
                tabKey={SettingsTab.GENERAL}
                disabled={isTabDisabled(SettingsTab.GENERAL)}
              />
              <Tab
                label="Profile"
                tabKey={SettingsTab.PROFILE}
                disabled={isTabDisabled(SettingsTab.PROFILE)}
              />
              <Tab
                label="API Key"
                tabKey={SettingsTab.API_KEY}
                disabled={isTabDisabled(SettingsTab.API_KEY)}
              />
            </Tabs>
          </PageHeader>
        }
      >
        <Container size="s">
          <Routes>
            {routes.map(route => (
              <Route
                key="route"
                path={route.path}
                element={
                  <RequireAuth
                    isAuthenticated={!!authUser}
                    isPermitted={
                      !route.requiredPermissions ||
                      (!!authUser && route.requiredPermissions.includes(authUser.permission))
                    }
                    providerExists={!!provider}
                    isAdminMode={isAdminMode}
                  >
                    {React.createElement(route.component)}
                  </RequireAuth>
                }
              />
            ))}
          </Routes>
        </Container>
      </PageContentLayout>
    </PagePartial>
  )
}

export default SettingsPage
